import { useQuery } from "@tanstack/react-query";
import cloneDeep from "lodash.clonedeep";
import { DateTime, Settings } from "luxon";

import { GET, TimesheetData } from "~utils";

import { getTimesheetEntryStatus } from "../components/TimesheetView";
import type { PayCode, TimeCardEntry, TimesheetEntry } from "../utils";

/**
 * Fetches timesheet data by interval ID
 */
export function useTimesheet(params: {
  // The week ID of the timesheet to fetch
  weekId: string | undefined;
  // Current time for determining timecard status (PAST_DUE, ACTION_REQUIRED, etc.)
  now: DateTime;
  // User's state for CA-specific behavior (CA users get empty times for
  // timecards, non-CA users get pre-populated request times)
  userState?: string;
}) {
  const { weekId, now, userState } = params;

  return useQuery({
    enabled: !!weekId,
    queryKey: ["timesheets", weekId],
    queryFn: () =>
      GET("/api/sub/timesheet/{interval-id}", { pathParams: { "interval-id": weekId! } }),
    // TODO: We are sorting the all timesheet entries, including the nested
    // `entries` field for the TimeCard type, and transforming all ISO strings
    // to DateTime objects.
    // @see https://linear.app/swing-education/issue/PDE-6764
    select: (data: { data: TimesheetData }) => {
      // Determine the timezone of the first school in the timesheet
      const timezone = data.data.find((entry) => entry.type === "TIMECARD")?.school.timeZone;

      // If there is a timezone, default all DateTime objects to this timezone
      if (timezone) {
        Settings.defaultZone = timezone;
      }

      const sortedTimesheetEntries = sortTransformTimesheetEntries(data.data);
      return populateEmptyTimesheetEntries({
        timesheetEntries: sortedTimesheetEntries,
        now,
        userState,
      });
    },
  });
}

/**
 * Given the timesheet data, return all timesheet entries sorted by oldest to
 * newest, including sorting the nested `entries` field for the TimeCard type.
 *
 * This also transforms all ISO strings to DateTime objects.
 *
 * TODO: The sorting of the timesheet entries, and the nested TimeCard entries
 * can be done at the API layer.
 */
function sortTransformTimesheetEntries(timesheetEntries: TimesheetData): Array<TimesheetEntry> {
  // Sort the timesheet entries placing the oldest entries at the top.
  const sortedTimesheetEntries = timesheetEntries.toSorted((a, b) => {
    const aTime =
      a.type === "TIMECARD"
        ? DateTime.fromISO(a.date).toMillis()
        : DateTime.fromISO(
            // For PayCode entries, use the date field if available, otherwise
            // use a date in the future so that this entry is sorted to the bottom.
            a.date ?? "2099-01-01",
          ).toMillis();
    const bTime =
      b.type === "TIMECARD"
        ? DateTime.fromISO(b.date).toMillis()
        : DateTime.fromISO(
            // For PayCode entries, use the date field if available, otherwise
            // use a date in the future so that this entry is sorted to the bottom.
            b.date ?? "2099-01-01",
          ).toMillis();
    return aTime - bTime;
  });

  // Then sort all TimeCard entries so that the oldest is at the top
  sortedTimesheetEntries
    .filter((entry) => entry.type === "TIMECARD")
    .forEach((timecard) => {
      timecard.entries = timecard.entries.toSorted((a, b) => {
        const aTime = DateTime.fromISO(
          // If a entry is missing a timestamp, set a newer date so that it is
          // sorted to the bottom.
          a.ts ?? "2099-01-01",
        ).toMillis();
        const bTime = DateTime.fromISO(
          // If a entry is missing a timestamp, set a newer date so that it is
          // sorted to the bottom.
          b.ts ?? "2099-01-01",
        ).toMillis();
        return aTime - bTime;
      });
    });

  // Lastly, update each ISO string to be a DateTime object.
  return sortedTimesheetEntries.reduce((acc, record) => {
    if (record.type === "TIMECARD") {
      acc.push({
        ...record,
        date: DateTime.fromISO(record.date),
        request: {
          ...record.request,
          startTime: DateTime.fromISO(record.request.startTime),
          endTime: DateTime.fromISO(record.request.endTime),
        },
        entries: record.entries.map((entry) => {
          return {
            ...entry,
            ts: entry.ts ? DateTime.fromISO(entry.ts) : undefined,
          };
        }),
      });
    } else {
      acc.push({
        ...record,
        payCode: record.payCode as PayCode["payCode"],
        date: record.date ? DateTime.fromISO(record.date) : undefined,
        durationInMinutes: record.durationInMinutes,
      });
    }
    return acc;
  }, [] as Array<TimesheetEntry>);
}

/**
 * Populates empty timesheet entries for TIMECARD records that require action.
 * Only processes TIMECARD entries with status "ACTION_REQUIRED" or null.
 *
 * For empty timecards:
 * - Creates PUNCH_IN and PUNCH_OUT entries
 * - For Non CA states: Sets timestamps to request start/end times
 * - For CA states: Do not set timestamps
 *
 * For shifts > 5 hours: Adds first meal break
 * - For Non CA states: Set the meal break time to be +4.5 hours from start,
 * and end time to be +5 hours from start
 * - For CA states: Do not set timestamps
 *
 * TODO: Implement Meal Break #2 (see logic below)
 * For shifts > 10 hours: Adds second meal break
 * - For Non CA states: Set the meal break time to be +9.5 hours from start,
 * and end time to be +10 hours from start
 * - For CA states: Do not set timestamps
 */
export function populateEmptyTimesheetEntries(params: {
  timesheetEntries: Array<TimesheetEntry>;
  now: DateTime;
  userState?: string;
}): Array<TimesheetEntry> {
  const { timesheetEntries, now, userState } = params;

  // For each timesheet entry (cloning to avoid mutating the original array)
  // NOTE: We are using `cloneDeep` from lodash to avoid mutating the original
  // array. Using `[...timesheetEntries]` only work on the first level of the
  // array, not the nested objects, they remain referenced. Using `window.structuredClone`
  // is also does not work since it doesn't handle Luxon DateTime objects. Thus
  // we are using `cloneDeep` from lodash to ensure that the original array is
  // not mutated while keeping all the prototypes the same.
  return cloneDeep(timesheetEntries).map((timesheet) => {
    // If they are not a TIMECARD, then ignore
    if (timesheet.type !== "TIMECARD") return timesheet;

    // Get the status of the TIMECARD
    const timeCardStatus = getTimesheetEntryStatus({
      timesheetEntry: timesheet,
      now,
    });

    // If the TIMECARD status not either `null` or `ACTION_REQUIRED`
    // Then ignore it.
    if (timeCardStatus !== "ACTION_REQUIRED" && timeCardStatus !== null) return timesheet;

    // Create old and new entries to store the edited entries
    const oldEntries = [...timesheet.entries];
    const newEntries: Array<TimeCardEntry> = [];

    /*** Create / Update Start & End Time ***/
    // Since every entry should have a `PUNCH_IN` and `PUNCH_OUT` entry which
    // represent the start and end time of the request, we can be sure that
    // there is at least two entries.

    // Create Start Time
    const startTimeEntry =
      // The first entry is the start time entry, so use that if it exists
      oldEntries[0]?.type === "PUNCH_IN"
        ? oldEntries.shift()!
        : {
            id: "",
            type: "PUNCH_IN" as const,
            ts: undefined,
          };
    // Set the proper timestamp for the start time entry
    startTimeEntry.ts =
      // If the start time entry has a timestamp, use that
      startTimeEntry.ts ??
      // Otherwise use the request start time if the user is not CA
      (userState !== "CA" ? timesheet.request.startTime : undefined);

    // Create End Time
    const endTimeEntry =
      // The last entry is the end time entry, so use that if it exists
      oldEntries[oldEntries.length - 1]?.type === "PUNCH_OUT"
        ? oldEntries.pop()!
        : // Otherwise create a new PUNCH_OUT entry
          {
            id: "",
            type: "PUNCH_OUT" as const,
            ts: undefined,
          };
    // Set the proper timestamp for the end time entry
    endTimeEntry.ts =
      // If the end time entry has a timestamp, use that
      endTimeEntry.ts ??
      // Otherwise use the request end time if the user is not CA
      (userState !== "CA" ? timesheet.request.endTime : undefined);

    // Add the start and end time entries to the new entries
    newEntries.push(startTimeEntry, endTimeEntry);

    /*** Create / Update Meal Break #1 ***/
    // If the request is more than 5 hours, the TimeCard will have a meal break
    if (timesheet.request.endTime.diff(timesheet.request.startTime).as("hours") <= 5) {
      // Break early is the request is less than 5 hours
      return {
        ...timesheet,
        entries: newEntries,
      };
    }

    // Create Meal Break #1 Start Time
    // Since we have reduced the old entries, the first entry is the meal break
    // start time entry. If it exists, use that.
    const mealBreak1StartTimeEntry =
      oldEntries[0]?.type === "PUNCH_OUT"
        ? oldEntries.shift()!
        : {
            id: "",
            type: "PUNCH_OUT" as const,
            ts: undefined,
          };
    mealBreak1StartTimeEntry.ts =
      // If the meal break start time entry has a timestamp, use that
      mealBreak1StartTimeEntry.ts ??
      // Otherwise use the request start time plus 4.5 hours if the user is not CA
      (userState !== "CA"
        ? timesheet.request.startTime.plus({ hours: 4, minutes: 30 })
        : undefined);

    // Create Meal Break #1 End Time
    // Since we have reduced the old entries, the first entry is the meal break
    // end time entry. If it exists, use that.
    const mealBreak1EndTimeEntry =
      oldEntries[0]?.type === "PUNCH_IN"
        ? oldEntries.shift()!
        : {
            id: "",
            type: "PUNCH_IN" as const,
            ts: undefined,
          };
    mealBreak1EndTimeEntry.ts =
      // If the meal break end time entry has a timestamp, use that
      mealBreak1EndTimeEntry.ts ??
      // Otherwise use the request start time plus 5 hours if the user is not CA
      (userState !== "CA" ? timesheet.request.startTime.plus({ hours: 5 }) : undefined);

    // Add the meal break start and end time entries to the new entries
    newEntries.splice(1, 0, mealBreak1StartTimeEntry, mealBreak1EndTimeEntry);

    // TODO: Implement Meal Break #2
    return {
      ...timesheet,
      entries: newEntries,
    };
  });
}
